'use client';
import React, { useState, Dispatch, SetStateAction, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { hasPermission } from 'utils/permissions';
import { t } from 'i18next';
import Search from 'components/common/Search';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import { useDebounce } from 'hooks/useDebounce';
import { DataIndexType, FiltersTimeline } from '../../types/timeline';
import { FilterTimeline, LeaveRequestFilter } from 'features/conge/components';
import { DemandeType, ExportFilterType } from '../../types/leaveRequestType';
import Link from 'next/link';
import { FiltersType } from 'components/common/DropdownFilter';
import { ExportCongeServices } from 'features/conge/services/Timeline';
import { downloadExcelBuffer } from 'utils/globalMethodes';
import CreateConge from '../CreateConge';
import { Moment } from 'moment';
import { Loader, MinthrSelect } from 'components/common';

interface LeaveRequestHeaderProps {
  timeLineFilters?: FiltersTimeline;
  setTimeLineFilters?: Dispatch<SetStateAction<FiltersTimeline>>;
  setFiltersDemande?: Dispatch<SetStateAction<DemandeType>>;
  dataIndex?: DataIndexType | null;
  selectedDate?: Moment;
  refetch?: () => void;
  demandeCount: number;
  congeCount: number;
}

const LeaveRequestHeader: React.FC<LeaveRequestHeaderProps> = ({
  timeLineFilters,
  setTimeLineFilters,
  setFiltersDemande,
  dataIndex,
  selectedDate,
  refetch,
  demandeCount,
  congeCount,
}) => {
  const path = usePathname();
  const [pageInfo, setPageInfo] = useState({
    page: path.includes('timeline') ? 'timeline' : 'demande',
    isActiveFilter: false,
  });
  const { openSidebar } = useSidebarManager();
  const [hasFilter, setHasFilter] = useState(false);
  const [isExportStart, setiIsExportStart] = useState<boolean>(false);
  const pathname = usePathname();
  const router = useRouter();

  const [tempFiltersTimeLine, setTempFiltersTimeLine] = useState<FiltersType>({
    filters: {
      sitesIds: [],
      departementIds: [],
      functionIds: [],
      society: [],
    },
  });

  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const handleSearch = (searchValue: string): void => {
    setSearchTerm(searchValue);
  };

  useEffect(() => {
    if (pageInfo.page === 'timeline' && setTimeLineFilters) {
      setTimeLineFilters((prev) => {
        return {
          ...prev,
          searchValue: debouncedSearchTerm,
        };
      });
    } else {
      if (setFiltersDemande) {
        setFiltersDemande((prev) => {
          if ('search' in prev) {
            return {
              ...prev,
              search: {
                ...prev.search,
                value: debouncedSearchTerm,
              },
            };
          }
          return prev;
        });
      }
    }
  }, [
    debouncedSearchTerm,
    pageInfo.page,
    setTimeLineFilters,
    setFiltersDemande,
  ]);

  const handelExportData = async (): Promise<void> => {
    if (!selectedDate) return;
    try {
      setiIsExportStart(true);
      const credentials: ExportFilterType = {
        dateMax: selectedDate.endOf('month').format('YYYY-MM-DD'),
        dateMin: selectedDate.startOf('month').format('YYYY-MM-DD'),
        departement: timeLineFilters?.filter.find(
          (item) => item.filterName === 'Departemant',
        )?.filterValue as string,
        site: timeLineFilters?.filter.find(
          (item) => item.filterName === 'Sites',
        )?.filterValue as string,
        society: timeLineFilters?.filter.find(
          (item) => item.filterName === 'society',
        )?.filterValue as string,
        service: timeLineFilters?.filter.find(
          (item) => item.filterName === 'Function',
        )?.filterValue as string,
        teams: timeLineFilters?.filter.find(
          (item) => item.filterName === 'Teams',
        )?.filterValue as string,
        fonction: timeLineFilters?.filter.find(
          (item) => item.filterName === 'Fonction',
        )?.filterValue as string,
        type: 'conges',
        ids: [],
      };
      const response = await ExportCongeServices(credentials);
      downloadExcelBuffer(
        response,
        `Conges_${selectedDate.format('MM_YYYY')}.xlsx`,
      );
    } catch (error) {
      console.error('Export failed', error);
    } finally {
      setiIsExportStart(false);
    }
  };

  const options = [
    {
      value: '/conge/timeline',
      label: (
        <Link
          className={`minthr-tabs-item ${pageInfo.page === 'timeline' ? 'minthr-mobile-color-active-tabs ' : ''}`}
          href="/conge/timeline"
        >
          {t('Timeline.UserInHoliday')}
          {pageInfo.page != 'timeline' && (
            <span className="minthr-badge minthr-badge-s minthr-badge-primary minthr-ml-2">
              {congeCount}
            </span>
          )}
        </Link>
      ),
    },
    {
      value: '/conge/demande',
      label: (
        <Link
          className={`minthr-tabs-item ${pageInfo.page === 'demande' ? 'minthr-mobile-color-active-tabs' : ''}`}
          href="/conge/demande"
        >
          {t('Conge.switch.link.demand')}
          {pageInfo.page == 'timeline' && (
            <span className="minthr-badge minthr-badge-s minthr-badge-primary minthr-ml-2">
              {demandeCount}
            </span>
          )}
        </Link>
      ),
    },
  ];

  return (
    <>
      <div className="minthr-content-header">
        <div className="minthr-flex minthr-flex-items-center">
          <div className="minthr-icon-bg minthr-icon-bg-l minthr-icon-bg-secondary minthr-icon-circle minthr-mr-3">
            <img className="minthr-icon-l" src="/icons/calendar.svg" alt="" />
          </div>
          <h2 className="minthr-mb-2">{t('Conge.Calender.title')}</h2>
        </div>
        {hasPermission('Conge.Create.Demand') && (
          <a
            className="minthr-btn minthr-btn-primary minthr-ml-auto minthr-mobile-ml-3 minthr-mobile-text-hide minthr-mobile-px-1 minthr-sidebar-open"
            onClick={() => openSidebar(CreateConge, true, { refetch })}
          >
            <img
              className="minthr-mr-1 minthr-mobile-mr-0"
              src="/icons/plus.svg"
              alt="Add Leave"
            />
            {t('Conge.ReserveForLater')}
          </a>
        )}
      </div>
      <div className="minthr-tabs minthr-flex minthr-flex-items-center minthr-flex-space-between minthr-mobile-flex-wrap minthr-mobile-border-none">
        <div className="minthr-mobile-hide minthr-tabs-nav">
          <Link
            className={`minthr-tabs-item ${pageInfo.page === 'timeline' ? 'active' : ''}`}
            href="/conge/timeline"
          >
            {t('Timeline.UserInHoliday')}
            {pageInfo.page != 'timeline' && congeCount > 0 && (
              <span className="minthr-badge minthr-badge-s minthr-badge-primary minthr-ml-2">
                {congeCount}
              </span>
            )}
          </Link>
          <Link
            className={`minthr-tabs-item ${pageInfo.page === 'demande' ? 'active' : ''}`}
            href="/conge/demande"
          >
            {t('Conge.switch.link.demand')}
            {
              <span className="minthr-badge minthr-badge-s minthr-badge-primary minthr-ml-2">
                {demandeCount}
              </span>
            }
          </Link>
        </div>

        <div className="minthr-hide minthr-mobile-show minthr-mb-3 w-100 minthr-mobile-border-b">
          <MinthrSelect
            borderNone={true}
            isSearchable={false}
            options={options}
            value={
              options
                .filter((item) => pathname.includes(item.value))
                .map((item) => ({
                  label: item.label,
                  value: item.value,
                }))[0]?.value
            }
            onchange={({ value }: { value: string }) => {
              router.push(value);
            }}
          />
        </div>

        <div className="minthr-searchbar minthr-searchbar-s white-bg minthr-ml-auto minthr-border-l minthr-mobile-border-none minthr-mobile-ml-0">
          <Search
            onSearch={handleSearch}
            placeholder={t('Search')}
            border={false}
          />
        </div>

        <a
          onClick={() =>
            setPageInfo((prev) => ({
              ...prev,
              isActiveFilter: !prev.isActiveFilter,
            }))
          }
          className={`minthr-icon-filter minthr-btn minthr-btn-outline-primary minthr-ml-3 p-0 minthr-border-none minthr-mobile-ml-1 minthr-btn-filters ${hasFilter && 'active'}`}
        >
          <img
            className={`${hasFilter ? '' : 'minthr-icon-gray'} `}
            src="/icons/filter.svg"
            alt="Filter"
          />
        </a>
        {pageInfo.page === 'timeline' && (
          <a
            className="minthr-btn minthr-btn-outline minthr-btn-light-gray minthr-ml-6 minthr-mobile-ml-0 minthr-mobile-w-100 minthr-mobile-mt-4"
            onClick={() => {
              handelExportData();
            }}
          >
            {isExportStart ? (
              <div className="">
                <Loader
                  className="w-auto minthr-mr-2 "
                  spinnerClass="minthr-icon-l"
                  spinnerInnerClass="minthr-icon-xxs"
                  icon="/icons/download.svg"
                />
              </div>
            ) : (
              <img className="minthr-mr-2" src="/icons/accept.svg" alt="" />
            )}

            {t('Btn.ExporConges')}
          </a>
        )}
      </div>

      <>
        {pageInfo.page === 'timeline' && setTimeLineFilters && (
          <FilterTimeline
            setTempFilters={setTempFiltersTimeLine}
            setTimeLineFilters={setTimeLineFilters}
            tempFilters={tempFiltersTimeLine}
            dataIndex={dataIndex}
            className={pageInfo.isActiveFilter ? '' : 'minthr-hide'}
          />
        )}

        {pageInfo.page === 'demande' && (
          <LeaveRequestFilter
            setFilters={setFiltersDemande}
            setHasFilter={setHasFilter}
            className={pageInfo.isActiveFilter ? '' : 'minthr-hide'}
          />
        )}
      </>
    </>
  );
};

export default LeaveRequestHeader;
